import {
  LitElement, html, nothing,
  type CSSResultGroup, type PropertyValues, type TemplateResult,
} from 'lit';
import { property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { repeat } from 'lit/directives/repeat.js';
import { emit } from '../utils/dom.utils.js';
import { panelStyles, stepperStyles } from './styles.js';

// Internal event names
const PANEL_CONNECTED = 'uwc-stepper-panel-connected';
const STEP_ACTION     = 'uwc-stepper-action';

// ══════════════════════════════════════════════════════════════════════════════
// uwc-stepper-panel
//
// Displays one step's content. In horizontal orientation the host is simply
// shown/hidden via CSS. In vertical orientation the host renders a full step
// row (indicator circle → connector line → header → animated content) and
// dispatches PANEL_CONNECTED / STEP_ACTION events to the parent stepper.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * uwc-stepper-panel
 *
 * A single step inside `uwc-stepper`.
 *
 * @attr {string}  header      - Step title.
 * @attr {string}  subheader   - Optional subtitle shown below the title.
 * @attr {string}  icon        - Bootstrap Icons name replacing the step number.
 * @attr {boolean} disabled    - Prevents navigating to this step.
 *
 * Internal (set by parent stepper — do not set manually):
 * @attr {boolean} active      - Whether this step is currently active.
 * @attr {boolean} completed   - Whether this step has been completed.
 * @attr {string}  orientation - Mirrors parent stepper orientation.
 * @attr {boolean} is-last     - Set on the final step (hides connector line).
 *
 * @slot default  - Step content.
 */
export class UwcStepperPanel extends LitElement {

  static override styles: CSSResultGroup = panelStyles;

  // ── Public properties ──────────────────────────────────────────────────────

  @property() header = '';
  @property() subheader = '';
  @property() icon?: string;
  @property({ type: Boolean, reflect: true }) disabled = false;

  // ── Internal — managed by UwcStepper ──────────────────────────────────────

  @property({ type: Boolean, reflect: true }) active    = false;
  @property({ type: Boolean, reflect: true }) completed = false;

  /**
   * Mirrors the parent stepper's `orientation` so panel CSS can differentiate.
   * Also controls whether this panel renders a full vertical row.
   */
  @property({ reflect: true }) orientation: 'horizontal' | 'vertical' = 'horizontal';

  /** Whether this is the last panel (removes the connector line). */
  @property({ type: Boolean, reflect: true, attribute: 'is-last' }) isLast = false;

  /** Whether non-linear click navigation is allowed (set by stepper). */
  @property({ type: Boolean, attribute: 'is-clickable' }) isClickable = false;

  /** Whether the stepper hides the built-in nav buttons (set by stepper). */
  @property({ type: Boolean, attribute: 'no-nav' }) noNav = false;

  // ── Zero-based index within the stepper (set by stepper) ──────────────────
  _stepIndex = 0;

  // ── Refs / animation ──────────────────────────────────────────────────────

  @query('.uwc-sp__v-content-wrap') private _wrap?: HTMLElement;
  private _currentAnimation?: Animation;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override connectedCallback() {
    super.connectedCallback();
    this.dispatchEvent(new CustomEvent(PANEL_CONNECTED, { bubbles: true, composed: true }));
  }

  override firstUpdated() {
    // In vertical mode, initially hide inactive panels without animation.
    if (this.orientation === 'vertical' && !this.active) {
      const wrap = this._wrap;
      if (wrap) {
        wrap.style.height   = '0px';
        wrap.style.overflow = 'hidden';
      }
    }
  }

  override updated(changed: PropertyValues) {
    super.updated(changed);
    if (!changed.has('active')) return;

    // Vertical: WAAPI animate open / close.
    // Horizontal: CSS display:none handles visibility — nothing to do here.
    if (this.orientation === 'vertical') {
      this.active ? this._animateOpen() : this._animateClose();
    }
  }

  // ── WAAPI animation (vertical mode) ───────────────────────────────────────

  private _animateOpen() {
    const wrap = this._wrap;
    if (!wrap) return;

    const fromH = this._currentAnimation ? wrap.getBoundingClientRect().height : 0;
    this._currentAnimation?.cancel();
    this._currentAnimation = undefined;

    // scrollHeight returns the full intrinsic content height even while
    // height:0 / overflow:hidden are applied — so we must NOT remove those
    // inline styles before measuring. Removing them first lets the browser
    // briefly render the content at full height, causing the visible jump.
    const toH = wrap.scrollHeight;

    const anim = wrap.animate(
      [
        { height: `${fromH}px`, overflow: 'hidden' },
        { height: `${toH}px`,   overflow: 'hidden' },
      ],
      { duration: this._dur(), easing: 'ease', fill: 'both' },
    );
    this._currentAnimation = anim;

    anim.addEventListener('finish', () => {
      if (this._currentAnimation !== anim) return;
      // Remove inline constraints BEFORE cancelling — while WAAPI fill still
      // holds the final height. Cancelling first would release the fill and
      // briefly expose the inline height:0 set by firstUpdated/_animateClose,
      // causing the visible jump at the end of the expand animation.
      wrap.style.removeProperty('height');
      wrap.style.removeProperty('overflow');
      anim.cancel();
      this._currentAnimation = undefined;
    }, { once: true });
  }

  private _animateClose() {
    const wrap = this._wrap;
    if (!wrap) return;

    const fromH = wrap.getBoundingClientRect().height;
    this._currentAnimation?.cancel();
    this._currentAnimation = undefined;

    const anim = wrap.animate(
      [
        { height: `${fromH}px`, overflow: 'hidden' },
        { height: '0px',        overflow: 'hidden' },
      ],
      { duration: this._dur(), easing: 'ease', fill: 'both' },
    );
    this._currentAnimation = anim;

    anim.addEventListener('finish', () => {
      if (this._currentAnimation !== anim) return;
      anim.cancel();
      wrap.style.height   = '0px';
      wrap.style.overflow = 'hidden';
      this._currentAnimation = undefined;
    }, { once: true });
  }

  private _dur() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 250;
  }

  // ── Event dispatchers ──────────────────────────────────────────────────────

  /** Fires when the user clicks the step header/indicator in vertical mode. */
  private _onHeaderClick() {
    if (this.disabled) return;
    this.dispatchEvent(new CustomEvent(STEP_ACTION, {
      bubbles:  true,
      composed: true,
      detail:   { action: 'select', stepIndex: this._stepIndex },
    }));
  }

  /** Fires when the built-in "Back" button inside a vertical step is clicked. */
  private _onPrev() {
    this.dispatchEvent(new CustomEvent(STEP_ACTION, {
      bubbles:  true,
      composed: true,
      detail:   { action: 'prev' },
    }));
  }

  /** Fires when the built-in "Next" button inside a vertical step is clicked. */
  private _onNext() {
    this.dispatchEvent(new CustomEvent(STEP_ACTION, {
      bubbles:  true,
      composed: true,
      detail:   { action: 'next' },
    }));
  }

  // ── Render helpers ─────────────────────────────────────────────────────────

  private _renderIndicatorContent(): TemplateResult {
    if (this.completed) return html`<uwc-icon name="check-lg" size="1.2em"></uwc-icon>`;
    if (this.icon)      return html`<uwc-icon name=${this.icon} size="1.2em"></uwc-icon>`;
    return html`${this._stepIndex + 1}`;
  }

  // ── Render: vertical layout ────────────────────────────────────────────────

  private _renderVertical(): TemplateResult {
    return html`
      <div class=${classMap({
        'uwc-sp__v-row':           true,
        'is-active':               this.active,
        'is-completed':            this.completed,
      })}>

        <!-- Left column: circle + vertical line -->
        <div class="uwc-sp__v-left">
          <button
            type="button"
            class=${classMap({
              'uwc-sp__v-indicator': true,
              'is-clickable':        this.isClickable && !this.disabled,
            })}
            aria-label=${`Step ${this._stepIndex + 1}: ${this.header}${this.completed ? ' (completed)' : this.active ? ' (current)' : ''}`}
            aria-current=${this.active ? 'step' : nothing}
            ?disabled=${this.disabled}
            tabindex=${this.isClickable && !this.disabled ? '0' : '-1'}
            @click=${this._onHeaderClick}
          >${this._renderIndicatorContent()}</button>

          ${!this.isLast ? html`
            <div class=${classMap({
              'uwc-sp__v-connector': true,
              'is-completed':        this.completed,
            })}></div>
          ` : nothing}
        </div>

        <!-- Right column: title + animated content -->
        <div class="uwc-sp__v-right">
          <button
            type="button"
            class=${classMap({
              'uwc-sp__v-header':  true,
              'is-clickable':      this.isClickable && !this.disabled,
            })}
            aria-expanded=${this.active ? 'true' : 'false'}
            aria-disabled=${this.disabled ? 'true' : nothing}
            tabindex=${this.isClickable && !this.disabled ? '0' : '-1'}
            @click=${this._onHeaderClick}
          >
            <span class=${classMap({
              'uwc-sp__v-title':   true,
              'is-active':         this.active,
              'is-completed':      this.completed,
            })}>${this.header}</span>
            ${this.subheader ? html`<span class="uwc-sp__v-subtitle">${this.subheader}</span>` : nothing}
          </button>

          <!-- Animated content -->
          <!-- inert keeps keyboard focus out while closed; height is always
               measured correctly because the nav is always in the DOM -->
          <div class="uwc-sp__v-content-wrap" role="region"
               aria-labelledby=${`uwc-sp-title-${this._stepIndex}`}
               ?inert=${!this.active}>
            <div class="uwc-sp__v-content">
              <slot></slot>
              ${!this.noNav ? html`
                <div class="uwc-sp__v-nav">
                  <uwc-button
                    variant="secondary"
                    outline
                    icon="arrow-left"
                    ?disabled=${this._stepIndex === 0}
                    @uwc-click=${this._onPrev}
                  >Back</uwc-button>
                  <uwc-button
                    icon="arrow-right"
                    icon-pos="right"
                    @uwc-click=${this._onNext}
                  >${this.isLast ? 'Finish' : 'Next'}</uwc-button>
                </div>
              ` : nothing}
            </div>
          </div>
        </div>
      </div>
    `;
  }

  // ── Render: horizontal layout (content only) ───────────────────────────────

  private _renderHorizontal(): TemplateResult {
    return html`
      <div
        class="uwc-sp__h-wrap"
        role="tabpanel"
        id=${`uwc-sp-panel-${this._stepIndex}`}
        aria-labelledby=${`uwc-sp-tab-${this._stepIndex}`}
      >
        <slot></slot>
      </div>
    `;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  override render() {
    return this.orientation === 'vertical'
      ? this._renderVertical()
      : this._renderHorizontal();
  }
}

// ══════════════════════════════════════════════════════════════════════════════
// uwc-stepper
//
// Manages a sequence of `uwc-stepper-panel` steps. In horizontal orientation
// it renders the step-indicators navigation bar itself; in vertical orientation
// the panels render their own rows and the stepper only wraps them. Both
// orientations share the same public API and event model.
// ══════════════════════════════════════════════════════════════════════════════

/**
 * uwc-stepper
 *
 * @attr {number}  active-step  - Zero-based index of the currently active step.
 * @attr {string}  orientation  - `"horizontal"` (default) | `"vertical"`.
 * @attr {boolean} linear       - Restrict navigation to sequential order only.
 * @attr {boolean} no-nav       - Hide built-in Back / Next buttons.
 *
 * @slot default - One or more `uwc-stepper-panel` elements.
 *
 * @fires uwc-step-change - Active step changed.
 *   `detail: { step: number, prevStep: number }`
 * @fires uwc-finish      - The Next button was clicked on the last step.
 *
 * @csspart nav         - The horizontal step-indicators nav bar.
 * @csspart content     - The horizontal content area.
 * @csspart footer      - The horizontal nav-buttons footer.
 *
 * @cssprop --uwc-step-active-bg          - Active indicator background. Default: primary.
 * @cssprop --uwc-step-active-border      - Active indicator border. Default: primary.
 * @cssprop --uwc-step-active-color       - Active indicator icon/number colour. Default: white.
 * @cssprop --uwc-step-done-bg            - Completed indicator background. Default: primary.
 * @cssprop --uwc-step-done-border        - Completed indicator border. Default: primary.
 * @cssprop --uwc-step-done-color         - Completed indicator icon colour. Default: white.
 * @cssprop --uwc-step-indicator-bg       - Inactive indicator background. Default: white.
 * @cssprop --uwc-step-indicator-border   - Inactive indicator border. Default: rgba(0,0,0,0.09).
 * @cssprop --uwc-step-indicator-color    - Inactive indicator text. Default: #64748b.
 * @cssprop --uwc-step-connector-color    - Separator line colour. Default: rgba(0,0,0,0.09).
 * @cssprop --uwc-step-connector-done     - Completed separator colour. Default: primary.
 * @cssprop --uwc-step-title-font-size    - Step title font size.
 * @cssprop --uwc-step-title-font-weight  - Step title font weight.
 * @cssprop --uwc-step-title-color        - Inactive step title colour.
 * @cssprop --uwc-step-active-title-color - Active step title colour.
 * @cssprop --uwc-step-done-title-color   - Completed step title colour.
 * @cssprop --uwc-step-content-padding    - Horizontal content area padding.
 * @cssprop --uwc-step-divider            - Horizontal divider line colour.
 */
export class UwcStepper extends LitElement {

  static override styles: CSSResultGroup = stepperStyles;

  // ── Public properties ──────────────────────────────────────────────────────

  @property({ type: Number, attribute: 'active-step' })
  activeStep = 0;

  @property({ reflect: true })
  orientation: 'horizontal' | 'vertical' = 'horizontal';

  /** If true, the user cannot skip steps by clicking future step indicators. */
  @property({ type: Boolean, reflect: true })
  linear = false;

  /** Hide the built-in Back / Next navigation buttons. */
  @property({ type: Boolean, attribute: 'no-nav' })
  noNav = false;

  // ── Internal state ─────────────────────────────────────────────────────────

  @state() private _panels: UwcStepperPanel[] = [];
  @state() private _activeIndex = 0;

  // ── Lifecycle ──────────────────────────────────────────────────────────────

  override connectedCallback() {
    super.connectedCallback();
    this.addEventListener(PANEL_CONNECTED, this._onPanelConnect as EventListener);
    this.addEventListener(STEP_ACTION,     this._onStepAction   as EventListener);
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener(PANEL_CONNECTED, this._onPanelConnect as EventListener);
    this.removeEventListener(STEP_ACTION,     this._onStepAction   as EventListener);
  }

  override firstUpdated() {
    this._syncPanels();
    this._apply(this.activeStep, false);
  }

  override updated(changed: PropertyValues) {
    super.updated(changed);

    if (changed.has('activeStep') && !changed.has('_activeIndex')) {
      this._apply(this.activeStep, false);
    }

    if (changed.has('orientation') || changed.has('linear') || changed.has('noNav')) {
      this._updatePanelMeta();
    }
  }

  // ── Panel management ───────────────────────────────────────────────────────

  private _syncPanels() {
    this._panels = Array.from(this.children).filter(
      (el): el is UwcStepperPanel => el instanceof UwcStepperPanel,
    );
    this._updatePanelMeta();
  }

  private _updatePanelMeta() {
    const n = this._panels.length;
    this._panels.forEach((p, i) => {
      p._stepIndex  = i;
      p.isLast      = i === n - 1;
      p.orientation = this.orientation;
      p.noNav       = this.noNav;
      p.isClickable = !this.linear && !p.disabled;
    });
  }

  // ── Core navigation logic ──────────────────────────────────────────────────

  private _apply(index: number, fireEvent = true) {
    const n = this._panels.length;
    if (n === 0) return;

    const next = Math.max(0, Math.min(index, n - 1));
    const prev = this._activeIndex;

    if (next === prev && fireEvent) return; // no-op

    this._activeIndex = next;

    this._panels.forEach((p, i) => {
      p.active    = i === next;
      p.completed = i < next;
    });

    if (fireEvent) {
      emit(this, 'uwc-step-change', { step: next, prevStep: prev });
    }
  }

  // ── Event handlers ─────────────────────────────────────────────────────────

  private _onPanelConnect() {
    this._syncPanels();
    // Re-apply active state once newly connected panels are registered.
    this._apply(this._activeIndex, false);
  }

  private _onStepAction(e: Event) {
    const detail = (e as CustomEvent).detail as { action: string; stepIndex?: number };

    switch (detail.action) {
      case 'select':
        if (!this.linear) {
          this._apply(detail.stepIndex ?? this._activeIndex);
        }
        break;
      case 'prev':
        this.prev();
        break;
      case 'next':
        this.next();
        break;
    }
  }

  private _onSlotChange() {
    this._syncPanels();
    this._apply(this._activeIndex, false);
  }

  // ── Step header click (horizontal nav bar) ─────────────────────────────────

  private _onStepClick(i: number) {
    if (this.linear) return;
    const panel = this._panels[i];
    if (!panel || panel.disabled) return;
    this._apply(i);
  }

  // ── Public API ─────────────────────────────────────────────────────────────

  /** Advance to the next step. Emits `uwc-finish` when already on the last step. */
  next() {
    if (this._activeIndex >= this._panels.length - 1) {
      emit(this, 'uwc-finish', {});
      return;
    }
    this._apply(this._activeIndex + 1);
  }

  /** Return to the previous step. No-op on the first step. */
  prev() {
    if (this._activeIndex <= 0) return;
    this._apply(this._activeIndex - 1);
  }

  /** Jump to a specific step (zero-based). In linear mode only the current
   *  step's Back/Next controls are respected; direct jumps are blocked. */
  goTo(index: number) {
    if (this.linear) return;
    this._apply(index);
  }

  /** Returns the zero-based index of the currently active step. */
  getActiveStep(): number { return this._activeIndex; }

  /** Returns the total number of steps. */
  getStepCount(): number { return this._panels.length; }

  // ── Getters ────────────────────────────────────────────────────────────────

  private get _isFirst() { return this._activeIndex === 0; }
  private get _isLast()  { return this._activeIndex >= this._panels.length - 1; }

  // ── Render helpers ─────────────────────────────────────────────────────────

  private _renderIndicatorContent(panel: UwcStepperPanel, i: number): TemplateResult {
    const isCompleted = panel.completed && i < this._activeIndex;
    if (isCompleted) return html`<uwc-icon name="check-lg" size="0.85em"></uwc-icon>`;
    if (panel.icon)  return html`<uwc-icon name=${panel.icon} size="1em"></uwc-icon>`;
    return html`${i + 1}`;
  }

  private _renderHorizontalNav(): TemplateResult {
    return html`
      <nav
        part="nav"
        class="uwc-stepper__nav"
        role="tablist"
        aria-label="Steps"
      >
        ${repeat(this._panels, (_, i) => i, (panel, i) => {
          const isActive    = i === this._activeIndex;
          const isCompleted = panel.completed && i < this._activeIndex;
          const isFuture    = i > this._activeIndex;
          const isDisabled  = panel.disabled || (this.linear && isFuture);
          const isClickable = !this.linear && !panel.disabled;

          return html`
            <div class="uwc-step">
              <button
                type="button"
                id=${`uwc-sp-tab-${i}`}
                class=${classMap({
                  'uwc-step__button': true,
                  'is-clickable':     isClickable,
                })}
                role="tab"
                aria-selected=${isActive ? 'true' : 'false'}
                aria-controls=${`uwc-sp-panel-${i}`}
                aria-disabled=${isDisabled ? 'true' : 'false'}
                aria-label=${`Step ${i + 1}: ${panel.header}${isCompleted ? ' (completed)' : isActive ? ' (current)' : ''}`}
                tabindex=${isActive ? '0' : '-1'}
                @click=${() => this._onStepClick(i)}
              >
                <span class=${classMap({
                  'uwc-step__indicator':           true,
                  'uwc-step__indicator--active':    isActive,
                  'uwc-step__indicator--completed': isCompleted,
                  'uwc-step__indicator--disabled':  isDisabled,
                })}>${this._renderIndicatorContent(panel, i)}</span>

                <div class="uwc-step__info">
                  <span class=${classMap({
                    'uwc-step__title':            true,
                    'uwc-step__title--active':    isActive,
                    'uwc-step__title--completed': isCompleted,
                  })}>${panel.header}</span>
                  ${panel.subheader ? html`
                    <span class="uwc-step__subtitle">${panel.subheader}</span>
                  ` : nothing}
                </div>
              </button>

              ${i < this._panels.length - 1 ? html`
                <div class=${classMap({
                  'uwc-step__separator':            true,
                  'uwc-step__separator--completed': isCompleted || (isActive && i < this._activeIndex),
                })} aria-hidden="true"></div>
              ` : nothing}
            </div>
          `;
        })}
      </nav>
    `;
  }

  private _renderHorizontalFooter(): TemplateResult {
    if (this.noNav) return html``;

    return html`
      <div part="footer" class="uwc-stepper__footer">
        <uwc-button
          variant="secondary"
          outline
          icon="arrow-left"
          ?disabled=${this._isFirst}
          @uwc-click=${() => this.prev()}
        >Back</uwc-button>

        <uwc-button
          icon="arrow-right"
          icon-pos="right"
          @uwc-click=${() => this.next()}
        >${this._isLast ? 'Finish' : 'Next'}</uwc-button>
      </div>
    `;
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  override render() {
    if (this.orientation === 'vertical') {
      return html`
        <div class="uwc-stepper--vertical">
          <slot @slotchange=${this._onSlotChange}></slot>
        </div>
      `;
    }

    return html`
      <div class="uwc-stepper--horizontal">
        ${this._renderHorizontalNav()}

        <div part="content" class="uwc-stepper__content" aria-live="polite">
          <slot @slotchange=${this._onSlotChange}></slot>
        </div>

        ${this._renderHorizontalFooter()}
      </div>
    `;
  }
}

// ── Global type augmentation ──────────────────────────────────────────────────

declare global {
  interface HTMLElementTagNameMap {
    'uwc-stepper':       UwcStepper;
    'uwc-stepper-panel': UwcStepperPanel;
  }
}
